import 'react-grid-layout/css/styles.css';
import React, { useEffect, useRef, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import type { Layout, LayoutItem, ResponsiveLayouts } from 'react-grid-layout/legacy';
import { LayoutDashboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { WidgetCard } from '../WidgetCard/WidgetCard';
import { changedLayoutUpdates } from '../../utils/gridLayout';
import type { WidgetDef, WidgetScope, WidgetLayoutUpdate } from '../../types';

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS = { lg: 960, md: 640, sm: 0 };

const RESIZE_HANDLE_STYLE: React.CSSProperties = {
  backgroundImage: 'none',
  bottom: 4,
  right: 4,
  width: 16,
  height: 16,
  backgroundColor: 'rgba(99,102,241,0.5)',
  borderRadius: 4,
  cursor: 'se-resize',
  zIndex: 20,
};
const COLS = { lg: 12, md: 6, sm: 2 };
const ROW_HEIGHT = 80;

const TYPE_MIN: Record<string, { minW: number; minH: number }> = {
  counter: { minW: 3, minH: 2 },
  pie: { minW: 4, minH: 3 },
  donut: { minW: 4, minH: 3 },
  bar: { minW: 4, minH: 3 },
  horizontal_bar: { minW: 4, minH: 3 },
  stacked_bar: { minW: 6, minH: 3 },
  line: { minW: 6, minH: 3 },
  area: { minW: 6, minH: 3 },
  table: { minW: 6, minH: 4 },
};

interface DashboardGridProps {
  widgets: WidgetDef[];
  scope: WidgetScope;
  isEditing: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  onEditWidget: (widget: WidgetDef) => void;
  onDeleteWidget: (widgetId: string) => void;
  onPersistLayout: (updates: WidgetLayoutUpdate[]) => void;
}

function toRglItem(widget: WidgetDef, breakpoint: 'desktop' | 'tablet' | 'mobile'): LayoutItem {
  // tablet/mobile may be null (backend does not auto-fill them) — fall back to desktop.
  const bp = widget.layout[breakpoint] ?? widget.layout.desktop;
  const { minW, minH } = TYPE_MIN[widget.type] ?? { minW: 2, minH: 2 };
  return { i: widget.id, x: bp.x, y: bp.y, w: bp.w, h: bp.h, minW, minH };
}

function buildLayouts(widgets: WidgetDef[]): ResponsiveLayouts {
  return {
    lg: widgets.map((w) => toRglItem(w, 'desktop')),
    md: widgets.map((w) => toRglItem(w, 'tablet')),
    sm: widgets.map((w) => ({ ...toRglItem(w, 'mobile'), w: 2 })),
  };
}

function DashboardGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-6 xl:grid-cols-12">
      {[3, 3, 3, 3].map((_, i) => (
        <div key={i} className="col-span-2 md:col-span-3">
          <Skeleton className="h-[160px] w-full rounded-lg" />
        </div>
      ))}
      {[0, 1].map((i) => (
        <div key={`chart-${i}`} className="col-span-2 md:col-span-6">
          <Skeleton className="h-[240px] w-full rounded-lg" />
        </div>
      ))}
    </div>
  );
}

function DashboardEmptyState() {
  const { t } = useTranslation('dashboard');
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-24 text-center"
      data-testid="dashboard-empty-state"
    >
      <LayoutDashboard className="h-12 w-12 text-muted-foreground" />
      <div>
        <h3 className="text-base font-semibold">{t('empty.title')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('empty.description')}</p>
      </div>
    </div>
  );
}

export function DashboardGrid({
  widgets,
  scope,
  isEditing,
  isAdmin,
  isLoading,
  onEditWidget,
  onDeleteWidget,
  onPersistLayout,
}: DashboardGridProps) {
  // Signature encodes id + desktop dimensions so the grid resets not only on
  // add/remove but also when a widget's size/position changes (e.g. after edit).
  const widgetKeys = widgets
    .map(
      (w) =>
        `${w.id}:${w.layout.desktop.x},${w.layout.desktop.y},${w.layout.desktop.w},${w.layout.desktop.h}`,
    )
    .join('|');
  const [prevWidgetKeys, setPrevWidgetKeys] = useState(widgetKeys);
  const [localLayouts, setLocalLayouts] = useState<ResponsiveLayouts>(() => buildLayouts(widgets));
  const pendingLayoutsRef = useRef<ResponsiveLayouts>(localLayouts);

  useEffect(() => {
    pendingLayoutsRef.current = localLayouts;
  }, [localLayouts]);

  // Derived state pattern: rebuild layouts from server data when the widget set
  // or any widget's size/position changes. React discards this render and
  // re-renders with the corrected layouts before the first browser paint.
  if (widgetKeys !== prevWidgetKeys) {
    setPrevWidgetKeys(widgetKeys);
    setLocalLayouts(buildLayouts(widgets));
  }

  if (isLoading) return <DashboardGridSkeleton />;

  if (widgets.length === 0) {
    return <DashboardEmptyState />;
  }

  // Update local state on every change (smooth visual feedback), no mutation here.
  const handleLayoutChange = (_: Layout, allLayouts: ResponsiveLayouts) => {
    setLocalLayouts(allLayouts);
    pendingLayoutsRef.current = allLayouts;
  };

  // Persist only once per interaction (drag end / resize end) to avoid
  // triggering query invalidation mid-drag — especially visible in mock mode
  // where the mutation resolves synchronously and would cause mid-drag re-renders.
  const handleInteractionEnd = () => {
    if (!isEditing) return;
    const updates = changedLayoutUpdates(widgets, pendingLayoutsRef.current);
    if (updates.length > 0) {
      onPersistLayout(updates);
    }
  };

  return (
    <div data-testid="dashboard-grid-root">
      <ResponsiveGridLayout
        className="layout"
        layouts={localLayouts}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        isDraggable={isEditing}
        isResizable={isEditing}
        draggableHandle=".drag-handle"
        onLayoutChange={handleLayoutChange}
        onDragStop={handleInteractionEnd}
        onResizeStop={handleInteractionEnd}
        margin={[16, 16]}
        resizeHandles={['se']}
        resizeHandle={
          ((axis: string, ref: React.Ref<HTMLSpanElement>) => (
            <span
              ref={ref}
              className={`react-resizable-handle react-resizable-handle-${axis}`}
              style={RESIZE_HANDLE_STYLE}
            />
          )) as unknown as React.ReactElement
        }
      >
        {widgets.map((widget) => (
          <div key={widget.id} className="h-full">
            <WidgetCard
              widget={widget}
              scope={scope}
              isEditing={isEditing}
              isAdmin={isAdmin}
              onEdit={onEditWidget}
              onDelete={onDeleteWidget}
            />
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
}
